<template>
  <div id="home">
    <div class="container px20 home-header-container">
      <site-header
        class="home-header"
        :heading="$t('Which event are you looking for?')"
        image="assets/home_banner.jpg"
        :home-page="true"
      />
    </div>

    <popular-artists
      v-if="homePageArtists"
      :title="$t('The most popular artists')"
      :description="$t('Some of the most popular artists on our website')"
      :category-artists="homePageArtists"
    />

    <lazy-hydrate when-idle>
      <section class="favorite-categories">
        <cms-block
          :identifier="'categories-you-may-like'"
          :lazy="false"
        />
      </section>
    </lazy-hydrate>

    <lazy-hydrate when-idle>
      <ev-category-block block-identifier="recommended-events" />
    </lazy-hydrate>

    <lazy-hydrate when-idle>
      <next-event />
    </lazy-hydrate>
  </div>
</template>

<script>
import NextEvent from 'theme/components/theme/blocks/NextEvent'
import SiteHeader from 'theme/components/theme/blocks/SiteHeader'
import CmsBlock from 'theme/components/core/blocks/Cms/Block'
import EvCategoryBlock from 'theme/components/theme/blocks/EvCategoryBlock'
import PopularArtists from 'src/modules/popular/component/PopularArtists'
import LazyHydrate from 'vue-lazy-hydration'
import { mapGetters } from 'vuex'
import { isServer } from '@vue-storefront/core/helpers'

export default {
  components: {
    NextEvent,
    SiteHeader,
    CmsBlock,
    EvCategoryBlock,
    LazyHydrate,
    PopularArtists
  },
  computed: {
    ...mapGetters({
      homePageArtists: 'popular/getHomePageArtists'
    })
  },
  async serverPrefetch () {
    await this.fetchPopularArtists()
  },
  async created () {
    if (!isServer) {
      await this.fetchPopularArtists()
    }
  },
  methods: {
    async fetchPopularArtists () {
      await this.$store.dispatch('popular/loadHomePageArtists')
    }
  },
  metaInfo () {
    return {
      meta: [
        { vmid: 'title', name: 'title', content: 'Köp biljetter till evenemanget' },
        { vmid: 'description', name: 'description', content: 'Hitta ditt nästa event! Bläddra, köp biljetter och njut av showen. Upptäck de mest populära evenemangen - konsert, teater, festival, sport, humor och show.' }
      ]
    }
  }
}
</script>
<style lang="scss" scoped>
@import "~theme/css/variables/grid";
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';

#home {
  /deep/ .bottom-header {
    .description {
      margin-bottom: 17px;
    }

    @include desktop {
      padding-bottom: 25px;

      h6 {
        font-size: inherit;
      }

      .description {
        margin-bottom: 2px;
      }
    }
  }

  /deep/.categories-you-may-like {
    .categories-you-may-like-description {
      @include tablet {
        font-size: 15px;
        line-height: 18px;
      }

      @include desktop {
        font-size: 24px;
        line-height: 29px;
      }
    }

    .category-heading {
      margin-bottom: 10px;

      @include mobile {
        font-size: 16px;
        line-height: 19px;
      }

      @include tablet {
        font-size: 16px;
        line-height: 19px;
      }

      @include desktop {
        font-size: 29px;
        line-height: 35px;
      }
    }
  }

  /deep/ .recommended-events {
    @include mediaQuery(sm) {
      h2 {
        margin-top: 9px;
      }
    }
  }

  .container {
    &.px20 {
      &.home-header-container {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }

  @include desktop {
    .container {
      &.px20 {
        &.home-header-container {
          padding-left: 85px;
          padding-right: 85px;
        }

        .home-header {
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;

          &::after {
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
          }

          /deep/.main-image {
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
          }
        }
      }
    }
  }

  /deep/.main-image {
    object-position: bottom;
  }
}

.bottom-header {
  max-height: 280px !important;
  min-height: 280px !important;

  @include desktop {
    max-height: 320px !important;
    min-height: 320px !important;
  }
}

.favorite-categories {
  /deep/ {
    p {
      margin: 0;
    }

    ul {
      list-style: none;
      overflow-x: scroll;
      -ms-overflow-style: none;
      scrollbar-width: none;
      margin: 0 auto;

      &::-webkit-scrollbar {
        display: none;
      }

      li {
        .li-content {
          overflow: visible;
        }

        img {
          object-fit: cover;
        }

        @include mobile {
          max-width: 120px;
        }
      }

      @include mediaQuery(sm) {
        padding-bottom: 30px;
      }

      a {
        padding-bottom: 155%;
        border-radius: 10px;
        overflow: hidden;

        &:focus {
          h5 {
            color: color(primary);
          }
        }
      }
    }

    .shadow-container {
      box-shadow: 0 15px 20px 0 rgba(#000, 0.4);
      border-radius: 50%;
      padding: 50%;
    }

    @include mediaQuery(sm) {
      .li-content {
        box-shadow: 0 10px 20px 0 rgba(#000, 0.2);
      }
    }

    @include mobile {
      .categories-you-may-like {
        h2 {
          text-align: left;
        }
      }
    }

    @include tablet {
      h2 {
        padding-top: 15px;
        margin-bottom: 0;
        padding-bottom: 5px;
      }
    }

    @include desktop {
      h2 {
        padding-top: 15px;
        margin-bottom: 0;
        padding-bottom: 5px;
        font-size: 40px;
        line-height: 48px;
      }

      .container {
        &.px20 {
          padding-left: 85px;
          padding-right: 85px;
        }
      }

      ul.container {
        overflow: auto;

        &.px20 {
          padding-left: 75px;
          padding-right: 75px;
        }
      }

      li {
        padding: 0 10px;

        a {
          border-radius: 20px;
        }

        .text-wrapper {
          h5 {
            margin-bottom: 21px;
            padding-left: 20px;
            font-size: 29px;
          }

          &:hover {
            h5 {
              color: color(primary);
            }
          }
        }
      }
    }
  }
}

</style>
